/**
 * Phase 101 — Issued Documents Ledger.
 *
 * Scoped to a single target (student or employee). Lists every letter ever
 * issued for them with a Re-Print action that re-opens the LetterheadShell
 * preview (no re-generation — uses the immutable archived body_text).
 */
import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, FileText, Loader2, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { downloadDocument } from '@/lib/downloadDocument';
import { LetterheadShell, printLetterhead } from './LetterheadShell';

export function DocumentLedger({ open, onClose, targetType, target, academy }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const fetchDocs = useCallback(async () => {
    if (!open || !target?.id) return;
    setLoading(true);
    try {
      const r = await api.get('/documents', { params: { target_type: targetType, target_id: target.id } });
      setDocs(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [open, target?.id, targetType]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  return (
    <>
      <Dialog open={!!open && !viewing} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="document-ledger-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Issued Documents — {target?.name || ''}</DialogTitle>
            <DialogDescription className="text-xs">Immutable archive of every letter generated for this profile.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground text-sm"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…</div>
          ) : docs.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm" data-testid="ledger-empty">No documents issued yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Ref</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead className="hidden md:table-cell">Type</TableHead>
                  <TableHead className="hidden md:table-cell w-32">Date</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`ledger-row-${d.id}`}>
                    <TableCell className="font-mono text-xs">{d.custom_id}</TableCell>
                    <TableCell className="text-sm">{d.subject}</TableCell>
                    <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-[10px]">{d.template_label}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{(d.created_at || '').slice(0, 10)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setViewing(d)} data-testid={`ledger-reprint-${d.id}`}>
                        <Printer className="h-3.5 w-3.5 mr-1" /> Re-print
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Re-print preview overlay */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ledger-reprint-dialog">
          <DialogHeader className="no-print">
            <DialogTitle className="flex items-center gap-2"><Printer className="h-4 w-4 text-primary" /> Re-print {viewing?.custom_id}</DialogTitle>
            <DialogDescription className="text-xs">{viewing?.template_label}</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <div className="flex items-center justify-end gap-2 no-print">
                <Button variant="ghost" size="sm" type="button" onClick={() => setViewing(null)}><X className="h-3.5 w-3.5 mr-1.5" /> Close</Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => setTimeout(printLetterhead, 150)}
                  data-testid="ledger-print-now"
                  title="Uses your browser print dialog. Backgrounds and logos may render differently across devices."
                >
                  <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Now
                </Button>
                <div className="flex flex-col items-end">
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => downloadDocument(api, `/documents/${viewing.id}/pdf`, `${viewing.custom_id || 'document'}.pdf`, { label: viewing.template_label })}
                    data-testid="ledger-download-pdf"
                    className="bg-slate-950 hover:bg-slate-800 text-white"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
                  </Button>
                  <span className="text-[10px] text-slate-400 mt-1 select-none">Recommended for best fidelity</span>
                </div>
              </div>
              <LetterheadShell
                academy={academy}
                subject={viewing.subject}
                addressedTo={viewing.addressed_to}
                bodyText={viewing.body_text}
                signingAuthority={viewing.signing_authority}
                documentId={viewing.custom_id}
                dateLabel={(viewing.created_at || '').slice(0, 10)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
